import {
  formatDate,
  getCurrencySymbol,
  NgIf,
  NgFor,
  AsyncPipe,
  LowerCasePipe,
} from '@angular/common';
import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import {
  PageDetailComponent,
  PrintPageComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import {
  Currency,
  LocaleService,
  TranslatePipe,
  AgrDatePipe,
  AgrNumberPipe,
} from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import {
  TableColumn,
  TableColumnTypes,
  TableConfig,
  TableFooterValueTypes,
  TableComponent,
} from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

import {
  getCustomerProducts,
  getProducts,
} from '../../../product/store/selectors/product.selectors';
import { EnergySystem, LysineSystem } from '../../../settings/models/settings.model';
import {
  getEnergySetting,
  getLysineSetting,
} from '../../../settings/store/selectors/settings.selectors';
import { Product } from '../../../shared/models/shared.model';
import {
  FeedAdvice,
  FeedAdviceProperties,
  FeedAdviceType,
  FeedingType,
  GenderTypes,
  Phase,
  PhaseTransitionTypes,
  ProductTotals,
  TableScheme,
  TerminalBoar,
} from '../../models/feed-advice.model';
import { FeedAdviceService } from '../../services/feed-advice.service';
import { SchemeSupportService } from '../../services/scheme-support.service';
import { upsertFeedAdviceRequest } from '../../store/feed-advice.actions';
import { getFeedAdvices, getSelectedFeedAdviceId } from '../../store/feed-advice.selectors';
import { FeedAdviceGraphComponent } from '../feed-advice-graph/feed-advice-graph.component';
import { FeedAdviceReportComponent } from '../feed-advice-report/feed-advice-report.component';
import { FeedAdviceSettingsComponent } from '../feed-advice-settings/feed-advice-settings.component';
import { FeedAdviceTableComponent } from '../feed-advice-table/feed-advice-table.component';
import { PriceSettingsComponent } from '../price-settings/price-settings.component';

type Orientation = 'portrait' | 'landscape';

@Component({
  selector: 'app-feed-advice-detail',
  templateUrl: './feed-advice-detail.component.html',
  styleUrls: ['./feed-advice-detail.component.scss'],
  imports: [
    PageDetailComponent,
    NgIf,
    MatSelect,
    MatOption,
    MatButton,
    MatIcon,
    PrintPageComponent,
    FeedAdviceReportComponent,
    NgFor,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    FormsModule,
    FeedAdviceSettingsComponent,
    FeedAdviceTableComponent,
    TableComponent,
    NotificationEmptyComponent,
    FeedAdviceGraphComponent,
    AsyncPipe,
    LowerCasePipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class FeedAdviceDetailComponent implements OnInit {
  pageTitle$: Observable<string> = this.store.select(RouterSelectors.getPath).pipe(
    map((route: string) => {
      if (route.indexOf('/view') !== -1) {
        this.mode = 'view';
        return this.locale.translate(
          `${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice-detail.view`,
        );
      } else if (route.indexOf('/edit') !== -1) {
        this.mode = 'edit';
        return this.locale.translate(
          `${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice-detail.edit`,
        );
      }
    }),
  );
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  energySetting$: Observable<EnergySystem> = this.store.select(getEnergySetting);
  lysineSetting$: Observable<LysineSystem> = this.store.select(getLysineSetting);

  @ViewChild(FeedAdviceGraphComponent) chartComponent: FeedAdviceGraphComponent;

  products$: Observable<Product[]> = combineLatest([
    this.store.select(getProducts),
    this.store.select(getSelectedFeedAdviceId),
    this.store.select(getFeedAdvices),
    this.store.select(getCustomerProducts),
    this.energySetting$,
    this.lysineSetting$,
  ]).pipe(
    skipWhile(
      ([existingProducts, _, feedAdvices, customerProducts, ___, ____]) =>
        existingProducts === null ||
        customerProducts === null ||
        feedAdvices === null ||
        feedAdvices === undefined ||
        feedAdvices.length === 0 ||
        (existingProducts?.length === 0 && customerProducts?.length === 0),
    ),
    map(
      ([
        existingProducts,
        feedAdviceId,
        feedAdvices,
        customerProducts,
        energySystem,
        lysineSystem,
      ]: [Product[], number, FeedAdvice[], Product[], EnergySystem, LysineSystem]) => {
        const feedAdvice = feedAdvices.find((x) => x.id === feedAdviceId);

        const products = [...(existingProducts ?? []), ...(customerProducts ?? [])];
        if (feedAdvice) {
          feedAdvice.phases.forEach((phase: Phase) => {
            if (products.findIndex((p: Product) => p.id === phase.product.id) < 0) {
              const product: Product = {
                ...phase.product,
                suitablePhases: [phase.phaseNumber],
                code: phase.product.code,
                energySystem,
                lysineSystem,
              };
              products.push(product);
            }
          });
        }
        return products;
      },
    ),
  );

  feedAdvice$: Observable<FeedAdvice> = combineLatest([
    this.store.select(getSelectedFeedAdviceId),
    this.store.select(UserSelectors.selectLocations),
    this.energySetting$,
    this.lysineSetting$,
    this.store.select(getFeedAdvices),
    this.products$,
  ]).pipe(
    skipWhile(
      ([feedAdviceId, ___, energySystem, lysineSystem, feedAdvices, products]) =>
        feedAdviceId === null ||
        energySystem === null ||
        lysineSystem === null ||
        feedAdvices === null ||
        (!!feedAdvices && feedAdvices.length === 0) ||
        (!!products && products.length === 0),
    ),
    map(
      ([feedAdviceId, userLocations, energySystem, lysineSystem, feedAdvices, _]: [
        number,
        SelectedLocation[],
        EnergySystem,
        LysineSystem,
        FeedAdvice[],
        Product[],
      ]) => {
        if (feedAdviceId) {
          const advice = feedAdvices.find((x) => x.id === feedAdviceId);
          if (!advice) {
            return undefined;
          }

          this.phaseTransition = advice.phaseTransition
            ? advice.phaseTransition
            : PhaseTransitionTypes.GRADUAL;
          this.advice = {
            ...advice,
            phaseTransition: this.phaseTransition,
            energySystem,
            lysineSystem,
          };
          this.feedAdviceType = advice.type;
          this.tableScheme = {
            phases: this.advice.phases,
            numberOfPhases: this.advice.numberOfPhases,
            phaseTransition: this.advice.phaseTransition,
            feedAdviceType: this.advice.type,
            feedAdviceLines: this.isWeanedPigletFeedadvice()
              ? this.advice.weanedPigletFeedAdviceLines
              : this.advice.fatteningRearingFeedAdviceLines,
            inputSystem: this.advice.inputSystem,
            pigletWeaningAge: this.isWeanedPigletFeedadvice() ? this.advice.weaningAge : null,
            rearingAge: this.isRearingGiltsFeedAdvice() ? this.advice.rearingAge : null,
          };
          this.locations = this.schemeSupportService.stringifyLocations(
            userLocations,
            advice.locationIds,
          );
          this.feedintake =
            this.feedAdviceType !== FeedAdviceType.WEANED_PIGLETS
              ? this.locale.translate(
                  `feed-advice-properties.feedintake.${advice.feedIntake.toLowerCase()}`,
                )
              : null;
          this.genders = this.schemeSupportService.stringifyGenders(
            advice.isBarrow,
            advice.isBoar,
            advice.isGilt,
          );
          this.weaningWeight = advice.weaningWeight;
          this.weaningAge = advice.weaningAge;
          this.rearingAge = advice.rearingAge;
          this.finalWeight = advice.finalWeight;

          this.feedAdviceProperties = <FeedAdviceProperties>{
            phaseTransition: this.advice.phaseTransition,
            terminalBoar: this.advice.terminalBoar,
            geneticLine: this.advice.geneticLine,
            feedAdviceType: this.advice.type,
            genders: this.schemeSupportService.getGendersArray(
              this.advice.isBarrow,
              this.advice.isBoar,
              this.advice.isGilt,
            ),
            feedintake: this.advice.feedIntake,
            phases: this.advice.numberOfPhases,
            inputSystem: this.advice.inputSystem,
            weaningWeight: this.advice.weaningWeight,
            weaningAge: this.advice.weaningAge,
            rearingAge: this.advice.rearingAge,
            selectedDosatorSettingsId: this.advice.selectedDosatorSettingsId,
            feedingType: this.advice.feedingType,
          };
          this.printOrientation = this.advice.showDosatorColumns ? 'landscape' : 'portrait';
          this.updateSummaryTable(true);
        }

        return this.advice;
      },
    ),
  );

  isReferenceFeedAdvice: boolean;
  showLysine: boolean;
  showEnergy: boolean;
  showDosator: boolean;
  printOrientation: Orientation;
  feedAdviceProperties: FeedAdviceProperties;
  phaseTransition: PhaseTransitionTypes;
  terminalBoars: string[] = Object.values(TerminalBoar);
  locations: string;
  feedintake: string;
  genders: string;
  feedAdviceType: string;
  weaningWeight: number;
  weaningAge: number;
  rearingAge: number;
  finalWeight: number;
  advice: FeedAdvice;
  tableScheme: TableScheme;
  hasTableValidationErrors = false;
  mode: string;
  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: true,
    scrolling: false,
    wrapperWidth: '100%',
  };
  productTotalsTableConfig: TableConfig;
  productTotalsColumns: TableColumn[] = [];
  productTotals: ProductTotals[] = [];
  currency: Currency = null;
  feedAdviceChanged = false;
  feedAdviceSettingsChanged = false;
  printSetting = 'fullsize';
  public readonly FeedAdviceType = FeedAdviceType;
  public readonly FeedingType = FeedingType;

  @ViewChild(FeedAdviceTableComponent) feedAdviceTableComponent: FeedAdviceTableComponent;
  @ViewChild(FeedAdviceSettingsComponent) settingsComponent: FeedAdviceSettingsComponent;

  constructor(
    private readonly store: Store<RootState>,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialogService: DialogService,
    private readonly schemeSupportService: SchemeSupportService,
    private readonly feedAdviceService: FeedAdviceService,
    private readonly router: Router,
    private readonly locale: LocaleService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.activatedRoute.data.subscribe((response) => {
      this.isReferenceFeedAdvice = <boolean>response.isReferenceFeedAdvice;
    });
  }

  ngOnInit(): void {
    this.feedAdvice$
      .pipe(
        skipWhile((feedAdvice) => !feedAdvice),
        take(1),
      )
      .subscribe((feedAdvice) => {
        this.productTotals = this.feedAdviceService.calculateProductTotals(feedAdvice);
        this.currency = this.locale.getCurrency();

        this.initColumns();
        this.initProductTotalsTable();
      });
  }

  setPrintSetting(event: MatSelectChange) {
    this.printSetting = <string>event.value;
  }

  isWeanedPigletFeedadvice(): boolean {
    return this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS;
  }

  isRearingGiltsFeedAdvice(): boolean {
    return this.feedAdviceType === FeedAdviceType.REARING;
  }

  private initProductTotalsTable() {
    this.productTotalsTableConfig = {
      ...this.tableConfigBase,
    };
    this.validateVisibiltyTotalNettoPriceColumn();
    this.productTotalsTableConfig.dataSource = new MatTableDataSource(this.productTotals);
  }

  private initColumns() {
    this.productTotalsColumns = [
      {
        id: 'productCode',
        type: TableColumnTypes.STRING,
        label: this.locale.translate('product-totals-table.columns.productCode'),
      },
      {
        id: 'productName',
        type: TableColumnTypes.STRING,
        label: this.locale.translate('product-totals-table.columns.productName'),
      },
      {
        id: 'ewKg',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'product-totals-table.columns.ewKg',
        )}  (${this.locale.translate('settings.energy.short.' + this.advice?.energySystem)})`,
        digitsInfo: '1.02-02',
      },
      {
        id: 'totalKg',
        type: TableColumnTypes.NUMBER,
        label: this.locale.translate('product-totals-table.columns.totalKg'),
        digitsInfo: '1.01-01',
      },
      {
        id: 'totalNettoPrice',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('product-totals-table.columns.totalNettoPrice')}
        (${getCurrencySymbol(this.currency, 'narrow')})`,
        footerValueType: TableFooterValueTypes.SUM,
        footerDescription: this.locale.translate('product-totals-table.footerDescription'),
      },
    ];
  }

  updateSummaryTable(initializing = false) {
    if (!initializing) {
      if (this.isWeanedPigletFeedadvice()) {
        this.advice.weanedPigletFeedAdviceLines = this.feedAdviceTableComponent.tableRows;
      } else {
        this.advice.fatteningRearingFeedAdviceLines = this.feedAdviceTableComponent.tableRows;
      }
      this.advice.phases = this.feedAdviceTableComponent.phases;
    }
    this.productTotals = this.feedAdviceService.calculateProductTotals(this.advice);
    this.productTotalsTableConfig = {
      ...this.productTotalsTableConfig,
      columns: this.productTotals.some((pt) => pt.totalNettoPrice > 0)
        ? this.productTotalsColumns
        : this.productTotalsColumns.filter((col) => col.id !== 'totalNettoPrice'),
      dataSource: new MatTableDataSource<ProductTotals>(this.productTotals),
    };

    this.hasTableValidationErrors =
      (this.feedAdviceTableComponent?.linesWithValidationErrors?.length ?? 0) > 0;

    this.tableScheme = {
      ...this.tableScheme,
      feedAdviceLines: this.isWeanedPigletFeedadvice()
        ? this.advice.weanedPigletFeedAdviceLines
        : this.advice.fatteningRearingFeedAdviceLines,
      phases: this.advice.phases,
      numberOfPhases: this.advice.numberOfPhases ?? 0,
    };

    this.cdr.detectChanges();

    if (this.chartComponent) {
      this.chartComponent.changeSetting();
    }
  }

  changeFeedAdviceSettings(emitted: { locations: number[]; properties: FeedAdviceProperties }) {
    const isGender = (genderType: string): boolean =>
      emitted.properties.genders
        ? emitted.properties.genders.some(
            (name) => name === this.locale.translate(`gender.${genderType}`).toLowerCase(),
          )
        : false;
    this.advice = {
      ...this.advice,
      locationIds: emitted.locations,
      phaseTransition: emitted.properties.phaseTransition,
      numberOfPhases: emitted.properties.phases,
      terminalBoar: emitted.properties.terminalBoar,
      geneticLine: emitted.properties.geneticLine,
      feedAdviceType: emitted.properties.feedAdviceType,
      type: emitted.properties.feedAdviceType,
      feedIntake: emitted.properties.feedintake,
      isBarrow: isGender(GenderTypes.BARROW),
      isBoar: isGender(GenderTypes.BOAR),
      isGilt: isGender(GenderTypes.GILT),
      inputSystem: emitted.properties.inputSystem,
      weaningWeight: emitted.properties.weaningWeight,
      weaningAge: emitted.properties.weaningAge,
      rearingAge: emitted.properties.rearingAge,
      selectedDosatorSettingsId: emitted.properties.selectedDosatorSettingsId,
      feedingType: emitted.properties.feedingType,
    };
    this.showLysine = emitted.properties.showLysine;
    this.showEnergy = emitted.properties.showEnergy;
    this.showDosator = emitted.properties.showDosator;

    this.printOrientation = this.showDosator ? 'landscape' : 'portrait';

    if (this.feedAdviceTableComponent) {
      this.feedAdviceTableComponent.updateFeedAdviceProperties(emitted.properties);
      this.updateSummaryTable();
    }

    this.feedAdviceChanged = true;
  }

  back() {
    if (this.feedAdviceChanged || this.feedAdviceSettingsChanged) {
      this.dialogService
        .pendingChanges({
          title: this.locale.translate('create-feed-advice.pending-changes-warning.title'),
          subTitle: this.locale.translate('create-feed-advice.pending-changes-warning.message'),
          pushHistoryState: false,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.router.navigate([`/${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`]);
          }
        });
    } else {
      this.router.navigate([`/${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`]);
    }
  }

  printFeedAdvice() {
    const existingTitle = document.title;
    const proposedTitle = `Pig ${this.locale.translate('feed-advice-title')} - ${formatDate(
      Date(),
      'dd-MM-yyyy',
      this.locale.getLocale(),
    )}`;
    if (window) {
      document.title = proposedTitle;
      window.print();
      document.title = existingTitle;
    }
  }

  getButtonText(type: string): string {
    return this.locale.translate(
      `${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice-${type}`,
    );
  }

  openPriceSettings() {
    this.dialogService
      .form(PriceSettingsComponent, {
        phases: this.feedAdviceTableComponent.phases,
        feedAdvice: this.advice,
      })
      .subscribe((result) => {
        if (result.result?.phases) {
          this.feedAdviceTableComponent.phases = result.result?.phases;
          this.advice.phases = result.result?.phases;
          this.updateSummaryTable();
          this.validateVisibiltyTotalNettoPriceColumn();
          this.feedAdviceSettingsChanged = true;
        }
      });
  }

  private validateVisibiltyTotalNettoPriceColumn() {
    this.productTotalsTableConfig.columns = this.productTotals.some((pt) => pt.totalNettoPrice > 0)
      ? this.productTotalsColumns
      : this.productTotalsColumns.filter((col) => col.id !== 'totalNettoPrice');
  }

  save() {
    this.settingsComponent.propertiesComponent.formGroup.updateValueAndValidity();
    this.settingsComponent.propertiesComponent.formGroup.markAllAsTouched();
    this.settingsComponent.propertiesComponent.formGroupRearingAge.updateValueAndValidity();
    this.settingsComponent.propertiesComponent.formGroupRearingAge.markAllAsTouched();

    this.feedAdviceTableComponent.validateTable();
    if (
      this.schemeSupportService.validateFeedAdvice(
        this.advice,
        this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
        this.feedAdviceType === FeedAdviceType.REARING,
        this.feedAdviceTableComponent,
        this.settingsComponent,
      )
    ) {
      this.advice = {
        ...this.advice,
        phases: this.feedAdviceTableComponent.phases,
        fatteningRearingFeedAdviceLines: !this.isWeanedPigletFeedadvice()
          ? this.feedAdviceTableComponent.tableRows
          : [],
        weanedPigletFeedAdviceLines: this.isWeanedPigletFeedadvice()
          ? this.feedAdviceTableComponent.tableRows
          : [],
        showDosatorColumns: this.showDosator,
        finalWeight: this.feedAdviceTableComponent.finalBodyWeightFormGroup.value.finalWeight,
      };
      this.store.dispatch(
        upsertFeedAdviceRequest({
          request: this.advice,
          isReferenceFeedAdvice: this.isReferenceFeedAdvice,
        }),
      );
    }
  }
}
